import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { AdequacyOutcomeModel } from "@app/shared/interfaces/adequacyOutcomeModel";
import { MultiMapExportAdequacyCombination, MultiMapExportAdequacyOutcome } from "@app/projects/network-assessment/multi-map-export/interfaces";
import ProjectService from "@app/projects/projectService";

export interface MultiMapExportAdequacyOutcomes {
  combinations: readonly MultiMapExportAdequacyCombination[];
  error: JQuery.jqXHR | null;
  isLoading: boolean;
  isSuccess: boolean;
}

const EMPTY_COMBINATIONS: readonly MultiMapExportAdequacyCombination[] = Object.freeze([]);

/**
 * The server reports the two requirements separately; the outcome names the requirement that FAILED, so
 * access met with servicing unmet is FailingServicingRequirement, not the reverse.
 */
export const classifyAdequacyOutcome = (accessMet: boolean, servicingMet: boolean): MultiMapExportAdequacyOutcome => {
  if (accessMet && servicingMet) return MultiMapExportAdequacyOutcome.Passing;
  if (accessMet) return MultiMapExportAdequacyOutcome.FailingServicingRequirement;
  if (servicingMet) return MultiMapExportAdequacyOutcome.FailingAccessRequirement;

  return MultiMapExportAdequacyOutcome.FailingBothRequirements;
};

/**
 * Fetched once for the page rather than per section: the right rail is the consumer, and it sits outside
 * the configuration sections.
 */
export const useMultiMapExportAdequacyOutcomes = (
  projectId?: number,
  projectRunConfigurationId?: number,
): MultiMapExportAdequacyOutcomes => {
  const projectService = useRef(new ProjectService()).current;

  const { data, error, isLoading, isSuccess } = useQuery<MultiMapExportAdequacyCombination[], JQuery.jqXHR>({
    enabled: !!projectId && !!projectRunConfigurationId,
    queryFn: async ({ signal }) => {
      const outcomes = await projectService
        .getAdequacyOutcomesByRunConfigurationId(String(projectId), String(projectRunConfigurationId), signal);

      return (outcomes ?? []).map<MultiMapExportAdequacyCombination>((outcome: AdequacyOutcomeModel) => ({
        outcome: classifyAdequacyOutcome(outcome.accessMet, outcome.servicingMet),
        regionSetId: outcome.regionSetId,
        specialtyGroupCode: outcome.specialtyGroupCode,
      }));
    },
    queryKey: ["multi-map-export", "adequacy-outcomes", projectId, projectRunConfigurationId],
    staleTime: 5 * 60 * 1000,
  });

  return { combinations: data ?? EMPTY_COMBINATIONS, error, isLoading, isSuccess };
};
